<script setup lang="ts">
import MbomAiPanel from './MbomAiPanel.vue';
import { computed, nextTick, onMounted, ref, watch } from 'vue';

import { eqsApi } from '@/api/eqs';
import { confirmAction } from '@/services/confirm';
import { notify } from '@/services/notice';
import { errorMessage } from '@/utils/format';

/**
 * 制造 BOM。
 *
 * 排布照着真实的 M-BOM Excel 来：**一个总成一张表**，用顶部的标签切换
 * （和 Excel 底部的工作表标签一个意思）；表内依次是总成段、散件材料段、喷漆段。
 *
 * 一开始是把 163 个段平铺成 163 张小卡片，滚半天看不出结构——
 * 「所有东西都挤在一块」（Tony 2026-08-18）。工程师看 BOM 是按总成看的，
 * 一次只关心一个总成，界面就该一次只给一个。
 *
 * MBOM 不手写，从 EBOM 生成：整体替换，因为它是 EBOM 的投影。
 */
const props = defineProps<{ projectId: string | number; canWrite: boolean; canConfirmPairing?: boolean }>();
const emit = defineEmits<{ (event: 'edit-quantity'): void; (event: 'locate-ebom', ref: string): void }>();

/**
 * 点了只读的用量格。
 *
 * 0821 实测：用量改只读之后，这一格点下去**什么都不发生**——没提示、没鼠标样式，
 * 用的人只会觉得"卡住了"（Tony 的原话就是"更改 mbom 会卡"，
 * 而服务器日志里一条 PATCH 都没有，说明根本没提交过）。
 * 只读可以，但必须说清楚为什么、以及去哪改。
 */
function explainReadonlyQty(): void {
  notify('info', '用量在 EBOM 改',
    '这里的用量是 EBOM 的数量按层级算出来的结果，改这里下次重算就没了。已为你切到 EBOM。');
  emit('edit-quantity');
}

interface MbomRow {
  id: number;
  partNumber: string;
  partName: string;
  joysonNo: string | null;
  customerPartNumber: string | null;
  unit: string | null;
  qtyNumerator: number | null;
  oldPartNumber?: string | null;
  fixedWastage?: number | null;
  sourceType?: string | null;
  cycleTimeS?: number | null;
  mouldNo?: string | null;
  productionLine?: string | null;
  logisticsOrg?: string | null;
  qtyDenominator: number | null;
  variableWastagePct: number | null;
  usageOrg: string | null;
  supplyOrg: string | null;
  issueOrg: string | null;
  ownerOrg: string | null;
  note: string | null;
  manual: boolean;
}
interface MbomTable {
  id: number;
  section: 'assembly' | 'material' | 'paint';
  parentPartNumber: string;
  /** 段头挂的东禾物料编码（0923）：这个件在本 M-BOM 里作为子项时的东禾号；认不出为 null */
  parentDongheNo?: string | null;
  parentPartName: string;
  parentJoysonNo: string | null;
  rootPartNumber: string;
  sheetName?: string | null;
  sheetOrder?: number | null;
  rootPartName: string;
  rows: MbomRow[];
}
interface Summary {
  tables: number; rows: number;
  assemblySections: number; materialSections: number; paintSections: number;
  needsConfirm: number; orgCounts: Record<string, number>;
}
interface EbomIssue {
  nodeId: number; partNumber: string; partName: string;
  kind: 'orphan-level' | 'missing-weight' | 'duplicate-child' | 'nested-part'; message: string;
}
interface Impact {
  inSync: boolean;
  summary: { sourceChanged: number; sourceDeleted: number; manual: number; uncovered: number };
  sourceChanged: Array<{ id: string; partNumber: string; partName: string }>;
  sourceDeleted: Array<{ id: string; partNumber: string; partName: string }>;
  uncovered: Array<{ id: string; partNumber: string; partName: string }>;
}

const loading = ref(true);
const error = ref('');
const tables = ref<MbomTable[]>([]);
const aiManufacturingRows = computed(() => Object.fromEntries(tables.value.flatMap(table => table.rows.map(row =>
  [`M${row.id}`, {...row, sheet:table.sheetName || table.rootPartName, parent:table.parentPartNumber}]))));
const summary = ref<Summary | null>(null);
const issues = ref<EbomIssue[]>([]);
const impact = ref<Impact | null>(null);
const generating = ref(false);
const query = ref('');
const aiRevision = ref(0);
const locatedRowId = ref<number | null>(null);
async function locateAiRow(ref: string): Promise<void> {
  const id = /^M\d+$/.test(ref) ? Number(ref.slice(1)) : NaN;
  const group = assemblies.value.find(a => a.tables.some(t => t.rows.some(r => r.id === id)));
  if (!group) {
    locatedRowId.value = null;
    notify('info', '原始行已变化', '未找到核验引用的制造 BOM 行，请重新核验；未跳转到同编码的其他行。');
    return;
  }
  query.value = ''; mouldFilter.value = ''; activeRoot.value = group.key; locatedRowId.value = id;
  await nextTick();
  document.querySelector(`[data-mbom-row="${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

/*
  按模具筛（0911 Tony:「模具/工装列增加筛选功能，方便同模具的产品信息填写」）。

  一套模具做好几个零件，填模具号时是**按模具**一批一批填的，而这张表是按总成
  排的——同模具的行散在各处，人得自己一行行认。筛出来就能连着填完一套再换下一套。

  `__blank__` 是「还没填的」：M-BOM 上这一列现在基本是空的，
  「把没填的挑出来」恰恰是最常用的一档。
*/
const MOULD_BLANK = '__blank__';
const mouldFilter = ref('');

function mouldOf(row: { mouldNo?: string | null }): string {
  return (row.mouldNo ?? '').trim();
}

/*
  层级错位的修复建议。

  体检只会说「把它挪到所属总成下」——挪到哪还是得人一行行看，X04C 那 28 处
  是手工判断改的。这里让系统把「挪到哪」也算出来：规则先按 Excel 行序、左右后缀、
  零件号连号、别的项目里同名件的挂法打分，分不出胜负才请 AI 在候选里挑一个。

  **一条都不自动落库**，每条都得人点「采纳」。BOM 错一位数是采购多买料。
*/
interface LevelAdvice {
  nodeId: number;
  partNumber: string;
  partName: string;
  currentParentName: string | null;
  suggestedParentId: number;
  suggestedParentNumber: string;
  suggestedParentName: string;
  reason: string;
  source: 'rule' | 'ai';
}
interface AdviceResult {
  advices: LevelAdvice[];
  unresolved: Array<{ nodeId: number; partNumber: string; partName: string; why: string }>;
  ruleCount: number;
  aiCount: number;
  aiUsed: boolean;
  issueCount: number;
}

const advice = ref<AdviceResult | null>(null);
const advising = ref(false);
/** 已经采纳或忽略掉的行，采纳后立刻从列表里消失，不用等整页刷新 */
const settled = ref<Set<number>>(new Set());
const adoptingId = ref<number | null>(null);

const openAdvices = computed(() =>
  (advice.value?.advices ?? []).filter((item) => !settled.value.has(item.nodeId)));

async function askAdvice(): Promise<void> {
  advising.value = true;
  try {
    advice.value = await eqsApi.adviseBomLevels(props.projectId) as unknown as AdviceResult;
    settled.value = new Set();
    const result = advice.value;
    if (!result.advices.length) {
      notify('success', '没有可给的建议',
        result.issueCount
          ? `报了 ${result.issueCount} 处错位，但这张 BOM 里找不到能收留它们的总成。`
          : '体检没有报层级错位。');
    } else {
      notify('success', `给了 ${result.advices.length} 条建议`,
        `${result.ruleCount} 条是规则直接判定的，${result.aiCount} 条是 AI 在候选里挑的。`
        + '每条都要你点采纳才会改。');
    }
  } catch (caught) {
    notify('error', '没能给出建议', errorMessage(caught));
  } finally {
    advising.value = false;
  }
}

async function adopt(item: LevelAdvice): Promise<void> {
  adoptingId.value = item.nodeId;
  try {
    await eqsApi.updateBomItem(props.projectId, item.nodeId, { parentId: item.suggestedParentId });
    settled.value = new Set(settled.value).add(item.nodeId);
    notify('success', `${item.partName} 已挪到 ${item.suggestedParentName} 下`,
      '层级变了，MBOM 要重新生成一次才会跟上。');
    await load();
  } catch (caught) {
    notify('error', '没挪成', errorMessage(caught));
  } finally {
    adoptingId.value = null;
  }
}

function ignoreAdvice(item: LevelAdvice): void {
  settled.value = new Set(settled.value).add(item.nodeId);
}
/** 当前看哪个总成；空串表示还没选，自动落到第一个 */
const activeRoot = ref('');

const SECTION_LABEL: Record<string, string> = {
  assembly: '总成件', material: '散件材料', paint: '喷漆',
};

/*
  分类别条。

  EBOM 是多表导入的（X04C 有 5 张工作表），类别条就是那 5 张表；
  MBOM 原来只按顶层总成分组、名字用零件名，两个页签看着对不上——
  0820 Tony 报的「MBOM 未同步」就是这个。
  现在 MBOM 行也带着工作表名，有就按它分、按它的顺序排，和 EBOM 完全一致；
  老数据没有工作表名，仍退回按顶层总成分组。
*/
const assemblies = computed(() => {
  const groups = new Map<string, {
    key: string; name: string; order: number; tables: MbomTable[]; rows: number;
  }>();
  const useSheets = tables.value.some((table) => !!table.sheetName);
  for (const [index, table] of tables.value.entries()) {
    const key = useSheets
      ? `sheet:${table.sheetName ?? '未分类'}`
      : (table.rootPartNumber || table.parentPartNumber);
    const name = useSheets
      ? (table.sheetName ?? '未分类')
      : (table.rootPartName || table.parentPartName);
    const order = useSheets ? (table.sheetOrder ?? index) : index;
    const group = groups.get(key) ?? { key, name, order, tables: [], rows: 0 };
    group.tables.push(table);
    group.rows += table.rows.length;
    groups.set(key, group);
  }
  return [...groups.values()].sort((a, b) => a.order - b.order);
});

const current = computed(() =>
  assemblies.value.find((item) => item.key === activeRoot.value) ?? assemblies.value[0] ?? null);

/** 段内顺序固定：总成 → 材料 → 喷漆，和真实表一致 */
const SECTION_ORDER: Record<string, number> = { assembly: 0, material: 1, paint: 2 };
/*
  先只应用搜索，模具那一维**留到后面**——下拉里的条数要从这一份算。

  算自己那一维时不带自己的条件：带上的话选中的那个永远是全部、
  其余永远是 0，下拉就没用了（0902 审计页栽过同一跤）。
*/
const searchedSections = computed(() => {
  if (!current.value) return [];
  const needle = query.value.trim().toLowerCase();
  return [...current.value.tables]
    .sort((left, right) => (SECTION_ORDER[left.section] ?? 9) - (SECTION_ORDER[right.section] ?? 9))
    .map((table) => ({
      ...table,
      rows: needle
        ? table.rows.filter((row) =>
          `${row.partNumber} ${row.partName} ${mouldOf(row)}`.toLowerCase().includes(needle))
        : table.rows,
    }));
});

/** 下拉里的选项和条数——和筛选用的是同一批行，不另算一遍 */
const mouldOptions = computed(() => {
  const counts = new Map<string, number>();
  let blank = 0;
  for (const table of searchedSections.value) {
    for (const row of table.rows) {
      const code = mouldOf(row);
      if (!code) { blank += 1; continue; }
      counts.set(code, (counts.get(code) ?? 0) + 1);
    }
  }
  const options = [...counts.entries()]
    .map(([value, count]) => ({ value, label: `${value}（${count}）`, count }))
    .sort((left, right) => left.value.localeCompare(right.value));
  if (blank) options.push({ value: MOULD_BLANK, label: `还没填（${blank}）`, count: blank });
  return options;
});

const currentSections = computed(() => {
  const picked = mouldFilter.value;
  return searchedSections.value
    .map((table) => ({
      ...table,
      rows: !picked
        ? table.rows
        : table.rows.filter((row) => (picked === MOULD_BLANK ? !mouldOf(row) : mouldOf(row) === picked)),
    }))
    .filter((table) => table.rows.length);
});

/*
  一行到底算不算命中：搜索 + 模具两维合在这一处判。

  0911 加模具筛选时差点又写成两套——搜索框认模具号了，而下面「别的总成里有没有」
  还按老口径（只看零件号和名称）算，搜模具号会得到「别处没有」，其实有。
  **一套模具做的零件本来就常常分散在不同总成下**，这条提示对模具筛选尤其要紧。
*/
function rowMatches(row: { partNumber: string; partName: string; mouldNo?: string | null }): boolean {
  const needle = query.value.trim().toLowerCase();
  if (needle && !`${row.partNumber} ${row.partName} ${mouldOf(row)}`.toLowerCase().includes(needle)) {
    return false;
  }
  const picked = mouldFilter.value;
  if (!picked) return true;
  return picked === MOULD_BLANK ? !mouldOf(row) : mouldOf(row) === picked;
}

/** 命中在别的总成里时给个跳转——否则搜不到会以为系统里没有 */
const hitsElsewhere = computed(() => {
  if (!query.value.trim() && !mouldFilter.value) return [];
  return assemblies.value.filter((group) =>
    group.key !== current.value?.key
    && group.tables.some((table) => table.rows.some(rowMatches)));
});

const blockingIssues = computed(() => issues.value.filter((item) => item.kind === 'orphan-level'));
const weightIssues = computed(() => issues.value.filter((item) => item.kind === 'missing-weight'));
const duplicateIssues = computed(() => issues.value.filter((item) => item.kind === 'duplicate-child'));
/** 零件下面挂零件：和层级错位一样是硬伤，会让 MBOM 多出总成段 */
const nestedIssues = computed(() => issues.value.filter((item) => item.kind === 'nested-part'));

const outOfSync = computed(() => {
  const stat = impact.value?.summary;
  return stat ? stat.sourceChanged + stat.sourceDeleted + stat.uncovered : 0;
});

/**
 * 1014 Tony: collapsed summary lists partNumber + partName.
 * Cap at PREVIEW_MAX; beyond that append "等共 N" so 140 missing-weight rows do not flood.
 */
const PREVIEW_MAX = 6;
type PartRef = { partNumber?: string; partName?: string; part_number?: string; part_name?: string };
function partLabel(item: PartRef): string {
  const no = (item.partNumber ?? item.part_number ?? '').trim();
  const name = (item.partName ?? item.part_name ?? '').trim();
  return [no, name].filter(Boolean).join(' ');
}
function previewParts(items: ReadonlyArray<PartRef>, max = PREVIEW_MAX): string {
  if (!items.length) return '';
  const labels = items.map(partLabel).filter(Boolean);
  if (!labels.length) return '';
  if (labels.length <= max) return labels.join('、');
  return `${labels.slice(0, max).join('、')} 等共 ${labels.length}`;
}
const hierarchyIssueItems = computed(() => [...blockingIssues.value, ...nestedIssues.value]);
const hierarchyPreview = computed(() => previewParts(hierarchyIssueItems.value));
const weightPreview = computed(() => previewParts(weightIssues.value));
const duplicatePreview = computed(() => previewParts(duplicateIssues.value));
function impactPreview(kind: 'sourceChanged' | 'sourceDeleted' | 'uncovered'): string {
  const list = impact.value?.[kind] ?? [];
  return previewParts(list);
}

/**
 * 读数据。
 *
 * `silent` 给「改完一格之后的刷新」用：不动 `loading`，避免整张表被拆掉重建
 * （拆了页面就塌成一行，滚动条弹回表头——0918 Tony 报的正是这个）。
 * 第一次读取照旧要挡住，不然人对着一张空表不知道是没数据还是在读。
 */
async function load(silent = false): Promise<void> {
  if (!silent) loading.value = true;
  error.value = '';
  try {
    const [mbom, inspect] = await Promise.all([
      eqsApi.projectMbom(props.projectId),
      eqsApi.inspectMbom(props.projectId).catch(() => ({ issues: [] })),
    ]);
    aiRevision.value++;
    tables.value = (mbom.tables ?? []) as unknown as MbomTable[];
    summary.value = (mbom.summary ?? null) as unknown as Summary | null;
    access.value = ((mbom as Record<string, unknown>).access ?? null) as ColumnAccess | null;
    issues.value = ((inspect as Record<string, unknown>).issues ?? []) as unknown as EbomIssue[];
  } catch (caught) {
    error.value = errorMessage(caught);
  } finally {
    if (!silent) loading.value = false;
  }
}

async function loadImpact(): Promise<void> {
  try {
    impact.value = await eqsApi.mbomImpact(props.projectId) as unknown as Impact;
  } catch {
    // 影响分析拿不到不该挡住整张表
    impact.value = null;
  }
}
onMounted(() => { void load(); void loadImpact(); });

// 数据换了之后原来选中的总成可能没了，落回第一个
watch(assemblies, (list) => {
  if (!list.some((item) => item.key === activeRoot.value)) {
    activeRoot.value = list[0]?.key ?? '';
  }
});

/**
 * 再生是整表重建（1024 Soft-Gate P0-1）：DH26003 再生后工艺格变「—」，现场反复问「数据没了」。
 * 确认框写清「将重建」「只带回已进保护集合的格子」，并算出当前能带回工艺的行数；为 0 时页面上挂黄条。
 */
const PROCESS_FIELDS = ['fixedWastage', 'variableWastagePct', 'cycleTimeS', 'mouldNo', 'productionLine'] as const;
const processRows = computed(() => tables.value.flatMap((table) => table.rows)
  .filter((row) => PROCESS_FIELDS.some((field) => row[field] !== null && row[field] !== undefined && row[field] !== '')).length);
const noProcessToKeep = computed(() => (summary.value?.rows ?? 0) > 0 && processRows.value === 0);

async function generate(): Promise<void> {
  const existing = summary.value?.rows ?? 0;
  if (!await confirmAction({
    title: existing ? '重建制造 BOM？' : '从 EBOM 生成 MBOM？',
    message: existing
      ? [
        `将重建制造 BOM：现有 ${existing} 行按当前 EBOM 整表重新生成。`,
        '只带回再生前已进保护集合的格子（按原父子关系匹配）；父子关系变了的行，工艺格会变成「—」。用量以 EBOM 为准；有冲突则停止更新。',
        `当前有工艺参数可带回的行：${processRows.value} / ${existing}。`,
        ...(processRows.value === 0 ? ['⚠ 当前没有可带回的工艺格，重建后工艺列全部为「—」，需要重新填写。'] : []),
        '强烈建议先「↓ 导出 M-BOM」留底，并查看 AI 核验后再重建。',
      ].join('\n')
      : '将按当前 EBOM 生成整套 MBOM。',
    confirmLabel: existing ? '已导出留底，确认重建' : '生成',
    danger: existing > 0,
  })) return;

  generating.value = true;
  try {
    const result = await eqsApi.generateMbom(props.projectId) as unknown as {
      assemblies: number; rows: number;
      stats: { orgCounts: Record<string, number>; needsWeightConfirm: number };
      droppedManualRows: Array<{ partName: string }>;
    };
    const orgs = Object.entries(result.stats?.orgCounts ?? {})
      .map(([org, count]) => `${org} ${count} 行`).join(' · ');
    notify('success', `生成了 ${result.rows} 行`,
      [orgs, result.droppedManualRows?.length
        ? `${result.droppedManualRows.length} 行人工补的内容已被替换掉` : ''].filter(Boolean).join('；'));
    await Promise.all([load(), loadImpact()]);
  } catch (caught) {
    notify('error', '生成失败', errorMessage(caught));
    aiRevision.value++;
  } finally {
    generating.value = false;
  }
}

/* ---------- 单元格就地改 ---------- */
/*
  只开放「生成算不准、要人拍板」的几列：用量分子/分母、三个组织、变动损耗、备注。
  零件号和父子关系不给改——那是 EBOM 的事，在这里改等于让两套 BOM 各说各话。
*/
type EditableField = 'qtyNumerator' | 'qtyDenominator' | 'variableWastagePct' | 'fixedWastage'
  | 'cycleTimeS' | 'mouldNo' | 'productionLine' | 'logisticsOrg'
  | 'supplyOrg' | 'issueOrg' | 'ownerOrg' | 'note';

/**
 * 每一列归谁（0827 Tony 的表头照片）。
 *
 * M-BOM 模板的表头第一行写着「E-BOM自动关联 / 应用工程师 / PMC工程师」——
 * 这一行以前只在纸上，系统里既看不见也管不着。现在由后端随树一起给：
 * 哪几格这个人点得动、每一列该找谁，都照它来，前端不自己按角色判第二遍。
 */
type ColumnOwner =
  | 'ebom' | 'appEngineer' | 'pmc' | 'quality' | 'project'
  | 'shopProcess' | 'processQuality';

/** 移交量产后改这一列要谁签（BOM 管理办法 6.4.4/6.4.5/6.4.6）；没移交时后端不下发 */
interface ApprovalRoute {
  clause: string;
  reviewers: string[];
  approvers: string[];
  cc: string[];
}

interface ColumnAccess {
  editableKeys: string[];
  /** 本项目量产移交了没有（GATE5 实际日期）；移交后表头换人、并多出签审链 */
  handedOver?: boolean;
  columns: Array<{
    label: string; handling: string; dept: string;
    owner: ColumnOwner;
    key: string | null; people: string[];
    approval?: ApprovalRoute;
  }>;
}
const access = ref<ColumnAccess | null>(null);

/*
  归属的中文名和后端 MBOM_OWNER_LABEL 同一套。
  0911 加「移交后换人」时后端多了三档（quality / shopProcess / processQuality），
  前端这份**没跟着加**——于是移交后表头上那一小行是空的，
  人只看到格子点不动，看不出为什么、也不知道该找谁。
*/
const OWNER_LABEL: Record<ColumnOwner, string> = {
  ebom: 'E-BOM 自动关联',
  appEngineer: '应用工程师',
  pmc: 'PMC 工程师',
  quality: '前期质量 / PQE',
  project: '项目团队',
  shopProcess: '车间工艺工程师',
  processQuality: '过程质量工程师',
};

/** 这一格点不点得动。后端没给 access 时按原样放行——新前端配旧后端不该把人锁死 */
function canEditField(field: EditableField): boolean {
  if (!props.canWrite) return false;
  if (!access.value) return true;
  return access.value.editableKeys.includes(field);
}

/** 表头上那一小行：这一列谁填。自己能改的列不标——标了满屏都是标签，反而看不出重点 */
function ownerTag(field: EditableField): string {
  const column = access.value?.columns.find((item) => item.key === field);
  if (!column || canEditField(field)) return '';
  // E-BOM 自动关联的那几列另有提示（点上去说「去 EBOM 改」），标签这儿不重复
  return column.owner === 'ebom' ? '' : OWNER_LABEL[column.owner] ?? '';
}

/** 已移交的项目里，改这一列要谁签——写成一句话，跟在归属后面 */
function approvalLine(route: ApprovalRoute | undefined): string {
  if (!route) return '';
  return `改完要签审（BOM 管理办法 ${route.clause}）：${route.reviewers.join('、')}审核，`
    + `${route.approvers.join('、')}批准`
    + (route.cc.length ? `，抄送${route.cc.join('、')}` : '') + '。';
}

/** 这一列归谁、该找谁；表头的 title 和点不动时的提示都用它 */
function columnHint(field: EditableField): string {
  const column = access.value?.columns.find((item) => item.key === field);
  if (!column) return '';
  if (column.owner === 'ebom') {
    return `${column.label}：跟着 E-BOM 自动关联，在这里改不了。要改去 EBOM 改完重新生成。`;
  }
  const who = column.people.length ? `请找 ${column.people.join('、')}` : '这个项目团队里还没有这个角色，请项目经理先加人';
  return `${column.label}：由${column.handling}填（${column.dept}）。${who}。${approvalLine(column.approval)}`;
}

/** 已移交时顶上那条说明：换了谁管、签审走哪几条 */
const handoverNotice = computed(() => {
  if (!access.value?.handedOver) return null;
  const routed = access.value.columns.filter((item) => item.approval);
  const byClause = new Map<string, { clause: string; labels: string[]; route: ApprovalRoute }>();
  for (const column of routed) {
    const key = column.approval!.clause;
    const entry = byClause.get(key) ?? { clause: key, labels: [], route: column.approval! };
    entry.labels.push(column.label);
    byClause.set(key, entry);
  }
  return [...byClause.values()].map((entry) => ({
    clause: entry.clause,
    labels: entry.labels.join('、'),
    line: approvalLine(entry.route),
  }));
});

const editing = ref<{ id: number; field: EditableField } | null>(null);
const editValue = ref('');
const ORG_CHOICES = ['东禾', '东元', '均源科技'];

function focusCell(el: unknown): void {
  if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement) el.focus();
}
function isEditing(row: MbomRow, field: EditableField): boolean {
  return editing.value?.id === row.id && editing.value.field === field;
}
function beginEdit(row: MbomRow, field: EditableField): void {
  if (!props.canWrite) return;
  /*
    不是你的列就别让它进编辑态，并**当场说清该找谁**——
    静悄悄地点不动，人只会以为界面卡了。
  */
  if (!canEditField(field)) {
    notify('info', '这一列不归你填', columnHint(field));
    return;
  }
  editing.value = { id: row.id, field };
  editValue.value = String(row[field] ?? '');
}
function cancelEdit(): void {
  editing.value = null;
  editValue.value = '';
}
async function commitEdit(row: MbomRow, field: EditableField, next?: string): Promise<void> {
  const value = (next ?? editValue.value).trim();
  const previous = String(row[field] ?? '');
  cancelEdit();
  if (value === previous) return;
  const numeric = ['qtyNumerator', 'qtyDenominator', 'variableWastagePct', 'fixedWastage', 'cycleTimeS'].includes(field);
  if (numeric && value !== '' && !Number.isFinite(Number(value))) {
    notify('error', '要填数字', `「${value}」不是数字。用量和损耗只收数字。`);
    return;
  }
  try {
    await eqsApi.updateMbomRow(props.projectId, row.id, {
      [field]: value === '' ? null : (numeric ? Number(value) : value),
    });
    // 静默刷新：表格留在 DOM 里，滚动位置和横向滚动都不动
    await Promise.all([load(true), loadImpact()]);
  } catch (caught) {
    notify('error', '这一格没存上', errorMessage(caught));
    await load(true);
  }
}

function needsConfirm(row: MbomRow): boolean {
  return /请确认/u.test(row.note ?? '');
}

/** 来源代码 → 中文；M-BOM 模板里这一列写「来源(自制/外购/外协)」 */
const SOURCE_LABEL: Readonly<Record<string, string>> = {
  make: '自制', buy: '外购', subcontract: '外协', standard: '标准件',
};

const exportUrl = computed(() => eqsApi.mbomExportUrl(props.projectId));
</script>

<template>
  <!--
    `loading && !tables.length` —— 只有**第一次**读取才用整屏提示挡住表格。

    0918 Tony：「在 mbom 里面更新节拍或者其他数据，每更新一个就会自动跳转到表头」。
    原来写的是 `v-if="loading"`：改完一格后 commitEdit 会重新拉一次数据，
    loading 一置 true，整张表被这一行 `<p>` 顶掉、页面高度当场塌成一行，
    浏览器把滚动条弹回顶部；数据回来表格再撑开，人已经在表头了。
    改一列就得重新滚一次，填一屏节拍要滚几十遍。

    这是 refresh-must-not-remount-panel 那条同一个坑（0905 通知栏栽过一次）：
    **有数据的刷新不许把面板拆掉重建**。表格留在 DOM 里，滚动位置自然就不动。
  -->
  <p v-if="loading && !tables.length" class="mbom-empty">正在读取制造 BOM…</p>
  <p v-else-if="error && !tables.length" class="mbom-error">{{ error }}</p>

  <template v-else>
    <MbomAiPanel :project-id="projectId" :can-write="canWrite" :can-confirm-pairing="canConfirmPairing" :revision="aiRevision" :manufacturing-rows="aiManufacturingRows" @locate="locateAiRow" @locate-ebom="emit('locate-ebom', $event)" />
    <section v-if="summary && summary.rows" class="bom-summary">
      <article><b>{{ assemblies.length }}</b><span>总成 · {{ summary.rows }} 个父子对</span></article>
      <article v-for="(count, org) in summary.orgCounts" :key="org">
        <b>{{ count }}</b><span>{{ org }}承接</span>
      </article>
      <article :class="{ warn: summary.needsConfirm > 0 }">
        <b>{{ summary.needsConfirm }}</b><span>用量待确认</span>
      </article>
    </section>

    <div v-if="outOfSync" class="mbom-issues risk">
      <b>EBOM 改过之后，MBOM 有 {{ outOfSync }} 处没跟上：</b>
      <ul>
        <li v-if="impact?.summary.sourceChanged">
          {{ impact.summary.sourceChanged }} 行的 EBOM 源已经改了
          <template v-if="impactPreview('sourceChanged')">：{{ impactPreview('sourceChanged') }}</template>
        </li>
        <li v-if="impact?.summary.sourceDeleted">
          {{ impact.summary.sourceDeleted }} 行的 EBOM 源已经删了，MBOM 还留着
          <template v-if="impactPreview('sourceDeleted')">：{{ impactPreview('sourceDeleted') }}</template>
        </li>
        <li v-if="impact?.summary.uncovered">
          EBOM 新增的 {{ impact.summary.uncovered }} 个零件还没有对应行
          <template v-if="impactPreview('uncovered')">：{{ impactPreview('uncovered') }}</template>
        </li>
      </ul>
      <template v-if="canWrite">重新生成一次就同步了。</template>
    </div>
    <p v-else-if="impact && summary?.rows" class="mbom-issues ok">
      <!--
        原来这里写「重新生成会被替换」，但实际是保留的（0821 Tony 指出）。
        说明和行为不一致比说明不全更糟——人会照着这句话去判断要不要点重算。
      -->
      ✓ 源 EBOM 数据已同步；重新生成仍需通过关系与参数保护检查<template v-if="impact.summary.manual">（{{ impact.summary.manual }} 行有人工改动：损耗/组织/备注会保留，用量按 EBOM 重算）</template>
    </p>

    <details v-if="issues.length" class="mbom-issues" :class="{ risk: blockingIssues.length + nestedIssues.length > 0 }">
      <summary>
        <b v-if="blockingIssues.length + nestedIssues.length">
          EBOM 有 {{ blockingIssues.length + nestedIssues.length }} 处层级对不上
          <template v-if="hierarchyPreview">：{{ hierarchyPreview }}</template>
        </b>
        <b v-else>EBOM 体检</b>
        <em>
          {{ blockingIssues.length + nestedIssues.length }} 层级 ·
          {{ duplicateIssues.length }} 重复料号<template v-if="duplicatePreview">（{{ duplicatePreview }}）</template>
          · {{ weightIssues.length }} 缺毛重<template v-if="weightPreview">（{{ weightPreview }}）</template>
        </em>
      </summary>
      <ul>
        <li v-for="item in blockingIssues" :key="`o${item.nodeId}`">
          <b>{{ item.partNumber }} {{ item.partName }}</b> — {{ item.message }}
        </li>
        <li v-for="item in nestedIssues" :key="`n${item.nodeId}`">
          <b>{{ item.partNumber }} {{ item.partName }}</b> — {{ item.message }}
        </li>
        <li v-for="item in duplicateIssues.slice(0, 5)" :key="`d${item.nodeId}`">
          <b>{{ item.partNumber }} {{ item.partName }}</b> — {{ item.message }}
        </li>
        <li v-if="weightIssues.length">
          {{ weightIssues.length }} 个零件只有净重、没有毛重或损耗——材料用量先按净重给并标「请确认」。
          <template v-if="weightPreview">涉及：{{ weightPreview }}。</template>
          毛重只能人填（AI 编不出来），在 EBOM 里勾出「毛重 g」补上，重新生成就不用再确认。
        </li>
      </ul>

      <!--
        「挪到哪」的建议。体检只说了「挪到所属总成下」，这里把哪个总成也算出来。
        每条都要人点采纳，一条都不自动改。
      -->
      <div v-if="canWrite && blockingIssues.length + nestedIssues.length" class="mbom-advice">
        <button
          type="button" class="button" :disabled="advising" @click="askAdvice"
        >{{ advising ? '正在看…' : advice ? '重新看一遍' : '看看这些该挂到哪' }}</button>
        <small v-if="!advice">
          先按 Excel 行序、左右后缀、零件号连号、别的项目里同名件的挂法打分，分不出胜负才问 AI。
          规则那部分拿三个项目 489 行真实 BOM 回测过：78 次拍板错 1 次。每条都要你点采纳才会改。
        </small>
        <small v-else>
          {{ advice.ruleCount }} 条规则判定（回测 98.7%） · {{ advice.aiCount }} 条 AI 判定{{
            advice.aiUsed ? '' : '（这次没用上 AI）' }}
        </small>
      </div>

      <ul v-if="openAdvices.length" class="mbom-advice-list">
        <li v-for="item in openAdvices" :key="item.nodeId">
          <div class="mbom-advice-move">
            <b>{{ item.partNumber }} {{ item.partName }}</b>
            <span>
              现在挂在 <em>{{ item.currentParentName ?? 'BOM 顶层' }}</em>
              → 建议挂到 <strong>{{ item.suggestedParentName }}</strong>
            </span>
          </div>
          <p>
            <span class="mbom-advice-tag" :class="item.source">
              {{ item.source === 'rule' ? '规则' : 'AI' }}
            </span>
            {{ item.reason }}
          </p>
          <div class="mbom-advice-act">
            <button
              type="button" class="button button-primary"
              :disabled="adoptingId === item.nodeId" @click="adopt(item)"
            >{{ adoptingId === item.nodeId ? '正在挪…' : '采纳' }}</button>
            <button type="button" class="inline-button" @click="ignoreAdvice(item)">忽略</button>
          </div>
        </li>
      </ul>

      <ul v-if="advice?.unresolved.length" class="mbom-advice-unresolved">
        <li v-for="item in advice.unresolved" :key="item.nodeId">
          {{ item.partNumber }} {{ item.partName }}：{{ item.why }}
        </li>
      </ul>
    </details>

    <section class="content-panel">
      <header class="section-heading">
        <div>
          <span>MANUFACTURING BOM</span>
          <h2>制造 BOM</h2>
          <p>
            一个总成一张表，和 M-BOM 的工作表对应。
            损耗、节拍、工装/模具、产线、三个组织和备注点一下就能直接改；<b>用量</b>、零件号和父子关系要回 EBOM 改。
          </p>
        </div>
        <div class="section-tools mbom-tools">
          <button
            v-if="canWrite" class="button button-secondary" type="button"
            :disabled="generating" @click="generate"
          >{{ generating ? '生成中…' : '⟳ 从 EBOM 重新生成' }}</button>
          <a v-if="summary && summary.rows" class="button button-primary" :href="exportUrl">
            ↓ 导出 M-BOM
          </a>
        </div>
      </header>
      <p v-if="canWrite && noProcessToKeep" class="mbom-regen-warn" data-regen-warn>
        当前制造 BOM 没有可带回的工艺格（损耗/节拍/模具/产线均为空）。「从 EBOM 重新生成」会整表重建，工艺列仍为「—」；重建前请先导出留底。
      </p>

      <!--
        已量产移交（0914 Tony 第 5 条）。BOM 管理办法 6.4.4/6.4.5/6.4.6 在移交后
        把三组列判给了不同的部门,并各带一条签审链。移交这件事必须写在明面上——
        不写的话,应用工程师只会发现自己昨天还能改的格子今天点不动了。
      -->
      <div v-if="handoverNotice" class="mbom-handover">
        <b>本项目已量产移交（GATE5 已填实际日期）</b>
        <p>按《BOM 管理办法》移交后换人维护，下面这几组列的填写人和签审链跟着换：</p>
        <ul>
          <li v-for="item in handoverNotice" :key="item.clause">
            <strong>{{ item.labels }}</strong>：{{ item.line }}
          </li>
        </ul>
      </div>

      <p v-if="!summary || !summary.rows" class="mbom-empty">
        还没有制造 BOM。<template v-if="canWrite">点右上「从 EBOM 生成」，系统会按零件结构拆成父子对，
        并把喷漆件的供应/发料/货主自动填成东元、电镀件填成均源科技。</template>
      </p>

      <template v-else>
        <!-- 总成标签：和 Excel 底部的工作表标签一个意思，一次只看一个总成 -->
        <nav class="mbom-roots" aria-label="选择总成">
          <button
            v-for="group in assemblies" :key="group.key" type="button"
            :class="{ on: group.key === current?.key }"
            @click="activeRoot = group.key"
          >
            <b>{{ group.name }}</b>
            <em>{{ group.rows }} 行</em>
          </button>
        </nav>

        <div class="mbom-toolbar">
          <input v-model="query" type="search" placeholder="在本表内搜零件号、名称或模具号">
          <!-- 按模具挑一批出来连着填（0911 第 2 页） -->
          <select aria-label="按模具或工装筛选" v-if="mouldOptions.length" v-model="mouldFilter" class="mbom-mould-filter">
            <option value="">模具/工装：全部</option>
            <option v-for="option in mouldOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
          <span v-if="current" class="mbom-current">
            <b>{{ current.name }}</b>
            <em>{{ current.tables.length }} 段 · {{ current.rows }} 行</em>
          </span>
        </div>

        <!-- 模具筛选也要出这条：同一套模具的零件常常跨总成（0911） -->
        <p v-if="(query || mouldFilter) && hitsElsewhere.length" class="mbom-hint">
          另有 {{ hitsElsewhere.length }} 个总成里也有匹配：
          <button
            v-for="group in hitsElsewhere.slice(0, 5)" :key="group.key"
            type="button" class="mbom-jump" @click="activeRoot = group.key"
          >{{ group.name }}</button>
        </p>

        <template v-if="current">
          <section v-for="table in currentSections" :key="table.id" class="mbom-section">
            <header>
              <span class="mbom-badge" :class="table.section">{{ SECTION_LABEL[table.section] }}</span>
              <!--
                段头挂东禾物料编码（0923 Tony 画的那根箭头：散件段的段头要和上面总成段里那一行对上；
                同一天「一起改吧」——喷漆段、子总成段、顶层总成都一样）。
                东禾号优先，认不出才退回零件号；客户号照旧挂在右边。
              -->
              <b :title="table.parentDongheNo && table.parentDongheNo !== table.parentPartNumber
                ? `东禾物料编码 ${table.parentDongheNo}（零件号列：${table.parentPartNumber}）` : undefined"
              >{{ table.parentDongheNo || table.parentPartNumber }}</b>
              <span class="mbom-parent-name">{{ table.parentPartName }}</span>
              <em v-if="table.parentJoysonNo">客户号 {{ table.parentJoysonNo }}</em>
              <em>{{ table.rows.length }} 行</em>
            </header>
            <div class="mbom-scroll">
              <table>
                <thead>
                  <tr>
                    <!--
                      表头按 M-BOM 模板从左到右排（0822 Tony 第 1 条「M-BOM 表头信息丢失」）：
                      项次|来源|客户物料编码|东禾物料编码|子项物料名称|子项单位|
                      用量:分子|用量:分母|固定损耗|变动损耗%|生产节拍/S|
                      工装/模具|产线|供应组织|发料组织|货主|备注（物流组织列已按肖娟要求取消）
                      工装/模具和产线是 0826 Tony 加的，标准模板里没有，位置紧跟生产节拍。
                    -->
                    <th>项次</th><th>来源</th>
                    <th>客户物料编码</th><th>东禾物料编码</th><th>子项物料名称</th><th>子项单位</th>
                    <th>用量:分子</th><th>用量:分母</th>
                    <th :title="columnHint('fixedWastage')">固定损耗<em>{{ ownerTag('fixedWastage') }}</em></th>
                    <th :title="columnHint('variableWastagePct')">变动损耗%<em>{{ ownerTag('variableWastagePct') }}</em></th>
                    <th :title="columnHint('cycleTimeS')">生产节拍/S<em>{{ ownerTag('cycleTimeS') }}</em></th>
                    <th :title="columnHint('mouldNo')">模具/工装<em>{{ ownerTag('mouldNo') }}</em></th>
                    <th :title="columnHint('productionLine')">产线<em>{{ ownerTag('productionLine') }}</em></th>
                    <!-- 2026-10-15 肖娟：取消「物流组织」列（只藏 UI；字段/再生保仍保留） -->
                    <th :title="columnHint('supplyOrg')">供应组织<em>{{ ownerTag('supplyOrg') }}</em></th>
                    <th :title="columnHint('issueOrg')">发料组织<em>{{ ownerTag('issueOrg') }}</em></th>
                    <th :title="columnHint('ownerOrg')">货主<em>{{ ownerTag('ownerOrg') }}</em></th>
                    <th>备注</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, index) in table.rows" :key="row.id"
                    :data-mbom-row="row.id" :class="{ manual: row.manual, confirm: needsConfirm(row), 'ai-located': locatedRowId === row.id }"
                  >
                    <td class="mbom-index">{{ index + 1 }}</td>
                    <td>{{ SOURCE_LABEL[row.sourceType ?? ''] ?? (row.sourceType || '—') }}</td>
                    <td>{{ row.customerPartNumber || row.joysonNo || '—' }}</td>
                    <!--
                      这一列是**东禾号**。0821 拆列之后新导入的项目 part_number 存的是均胜号，
                      原来把它加粗顶在前面，于是「客户物料编码」和「东禾物料编码」显示同一个号，
                      看上去就是「两边没关联上」（0826 Tony）。有东禾号就显示东禾号。
                    -->
                    <td><b>{{ row.oldPartNumber || row.partNumber }}</b></td>
                    <td>{{ row.partName }}</td>
                    <td>{{ row.unit }}</td>
                    <!--
                      用量只读（0821 Tony 定）：它是 EBOM 的用量按层级算出来的**结果**，
                      在结果上改，下次重新生成不是被冲掉就是留下重复行。要改用量去 EBOM 改。
                      损耗、三个组织、备注仍然可以改——那些本来就是 M-BOM 上的工艺信息。
                    -->
                    <td
                      class="mbom-qty is-locked"
                      title="用量 = EBOM 的数量按层级算出来的结果。要改去 EBOM 改。"
                      @click="explainReadonlyQty"
                    >{{ row.qtyNumerator ?? '—' }}</td>
                    <td
                      class="mbom-qty is-locked"
                      title="用量分母同样来自 EBOM 计算结果"
                      @click="explainReadonlyQty"
                    >{{ row.qtyDenominator ?? 1 }}</td>
                    <!-- 固定损耗：模板里在变动损耗前面，之前界面上根本没有这一列 -->
                    <td
                      class="cell-edit"
                      :class="{ 'is-editing': isEditing(row, 'fixedWastage'), 'not-mine': !canEditField('fixedWastage') }"
                      @click="beginEdit(row, 'fixedWastage')"
                    >
                      <input
                        v-if="isEditing(row, 'fixedWastage')"
                        :ref="focusCell" v-model="editValue" type="text"
                        @keydown.enter="commitEdit(row, 'fixedWastage')"
                        @blur="commitEdit(row, 'fixedWastage')"
                        @keydown.esc="cancelEdit"
                      >
                      <template v-else>{{ row.fixedWastage ?? '—' }}</template>
                    </td>
                    <td
                      class="cell-edit"
                      :class="{ 'is-editing': isEditing(row, 'variableWastagePct'), 'not-mine': !canEditField('variableWastagePct') }"
                      @click="beginEdit(row, 'variableWastagePct')"
                    >
                      <input
                        v-if="isEditing(row, 'variableWastagePct')"
                        :ref="focusCell" v-model="editValue" type="text"
                        @keydown.enter="commitEdit(row, 'variableWastagePct')"
                        @blur="commitEdit(row, 'variableWastagePct')"
                        @keydown.esc="cancelEdit"
                      >
                      <template v-else>{{ row.variableWastagePct ?? '—' }}</template>
                    </td>
                    <!-- 生产节拍：库里一直有 cycle_time_s，界面上没露出来 -->
                    <td
                      class="cell-edit"
                      :class="{ 'is-editing': isEditing(row, 'cycleTimeS'), 'not-mine': !canEditField('cycleTimeS') }"
                      @click="beginEdit(row, 'cycleTimeS')"
                    >
                      <input
                        v-if="isEditing(row, 'cycleTimeS')"
                        :ref="focusCell" v-model="editValue" type="text"
                        @keydown.enter="commitEdit(row, 'cycleTimeS')"
                        @blur="commitEdit(row, 'cycleTimeS')"
                        @keydown.esc="cancelEdit"
                      >
                      <template v-else>{{ row.cycleTimeS ?? '—' }}</template>
                    </td>
                    <!--
                      工装/模具、产线（0826 Tony）：紧跟生产节拍，都是自由文本。
                      工装/模具存的就是 EBOM 那一列 mould_no（那边叫「模具编号」），
                      同一件事不再开第二列；两套 BOM 是两行数据，各填各的。
                    -->
                    <td
                      v-for="field in (['mouldNo', 'productionLine'] as const)"
                      :key="field"
                      class="cell-edit"
                      :class="{ 'is-editing': isEditing(row, field), 'not-mine': !canEditField(field) }"
                      @click="beginEdit(row, field)"
                    >
                      <input
                        v-if="isEditing(row, field)"
                        :ref="focusCell" v-model="editValue" type="text"
                        :placeholder="field === 'mouldNo' ? '工装/模具号' : '产线'"
                        @keydown.enter="commitEdit(row, field)"
                        @blur="commitEdit(row, field)"
                        @keydown.esc="cancelEdit"
                      >
                      <template v-else>{{ row[field] || '—' }}</template>
                    </td>
                    <td
                      v-for="field in (['supplyOrg', 'issueOrg', 'ownerOrg'] as const)"
                      :key="field"
                      class="cell-edit"
                      :class="{ 'org-other': row[field] && row[field] !== '东禾', 'is-editing': isEditing(row, field), 'not-mine': !canEditField(field) }"
                      @click="beginEdit(row, field)"
                    >
                      <select
                        v-if="isEditing(row, field)"
                        :ref="focusCell" :value="row[field] ?? ''"
                        @change="commitEdit(row, field, ($event.target as HTMLSelectElement).value)"
                        @blur="cancelEdit"
                        @keydown.esc="cancelEdit"
                      >
                        <option value="">未定</option>
                        <option v-for="org in ORG_CHOICES" :key="org" :value="org">{{ org }}</option>
                      </select>
                      <template v-else>{{ row[field] || '—' }}</template>
                    </td>
                    <td
                      class="mbom-note cell-edit"
                      :class="{ 'is-editing': isEditing(row, 'note'), 'not-mine': !canEditField('note') }"
                      @click="beginEdit(row, 'note')"
                    >
                      <input
                        v-if="isEditing(row, 'note')"
                        :ref="focusCell" v-model="editValue" type="text" placeholder="备注"
                        @keydown.enter="commitEdit(row, 'note')"
                        @blur="commitEdit(row, 'note')"
                        @keydown.esc="cancelEdit"
                      >
                      <template v-else>
                        <span v-if="row.manual" class="mbom-manual">人工改过</span>
                        {{ row.note || '' }}
                      </template>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
          <p v-if="!currentSections.length" class="mbom-empty">
            这个总成里没有匹配「{{ query }}」的零件。
          </p>
        </template>
      </template>
    </section>
  </template>
</template>

<style scoped>
tr.ai-located > td { background: #fff0bb; box-shadow: inset 0 2px #b77900, inset 0 -2px #b77900; }
tr.ai-located { scroll-margin-top: 90px; }
.mbom-handover {
  margin: 0 0 14px; padding: 12px 14px;
  border: 1px solid var(--line); border-left: 3px solid var(--blue);
  border-radius: var(--radius-s); background: var(--white);
}
.mbom-handover b { font-size: 13px; }
.mbom-handover p { margin: 4px 0 8px; color: var(--muted); font-size: 12px; line-height: 1.7; }
.mbom-handover ul { margin: 0; padding-left: 18px; display: grid; gap: 4px; }
.mbom-handover li { color: var(--muted); font-size: 12px; line-height: 1.7; }
.mbom-handover strong { color: var(--ink); }
.mbom-empty { margin: 0; padding: 18px 0; color: var(--muted); font-size: 13px; line-height: 1.8; }
.mbom-error { margin: 0; padding: 12px; color: var(--risk); font-size: 13px; }
/* 再生前没有可带回的工艺格：黄条提醒，不挡操作（1024 P0-1） */
.mbom-regen-warn { margin: 0 0 12px; padding: 8px 13px; border-left: 3px solid #b77900; background: #FFF6DB; color: #7a5200; font-size: 12px; line-height: 1.8; }

.mbom-issues {
  margin-bottom: 12px; padding: 10px 13px;
  border-left: 3px solid var(--blue); background: #EEF4FA;
  font-size: 12px; line-height: 1.8;
}
.mbom-issues.risk { border-left-color: var(--risk); background: #FDF1EF; }
.mbom-issues.ok { border-left-color: #2f7d32; background: #F1F8F2; }
.mbom-issues ul { margin: 6px 0 0; padding-left: 18px; max-height: 220px; overflow: auto; }

/* 「挪到哪」的建议：一条一张卡，改什么一眼看得见，采纳是人点的 */
.mbom-advice { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin: 12px 0 0; }
.mbom-advice small { flex: 1 1 260px; color: var(--muted); font-size: 11px; line-height: 1.6; }
.mbom-advice-list {
  display: grid; gap: 8px; margin: 10px 0 0; padding: 0;
  list-style: none; max-height: 360px; overflow: auto;
}
.mbom-advice-list li {
  display: grid; gap: 6px; padding: 10px 12px;
  border: 1px solid var(--line); border-left: 3px solid var(--blue);
  border-radius: var(--radius-s); background: var(--white);
}
.mbom-advice-move { display: grid; gap: 2px; }
.mbom-advice-move b { font-size: 13px; }
.mbom-advice-move span { color: var(--muted); font-size: 12px; }
.mbom-advice-move em { font-style: normal; }
.mbom-advice-move strong { color: var(--blue); }
.mbom-advice-list p { margin: 0; color: var(--ink); font-size: 12px; line-height: 1.7; }
.mbom-advice-tag {
  display: inline-block; margin-right: 6px; padding: 1px 7px; border-radius: 999px;
  font-size: 11px; font-weight: 700;
}
/* 规则和 AI 分开标：一条建议是算出来的还是模型挑的，判断时分量不一样 */
.mbom-advice-tag.rule { background: #eef4ea; color: #2f7d32; }
.mbom-advice-tag.ai { background: #eaf1fa; color: var(--blue); }
.mbom-advice-act { display: flex; align-items: center; gap: 10px; }
.mbom-advice-unresolved {
  margin: 10px 0 0; padding-left: 18px; color: var(--muted); font-size: 12px; line-height: 1.7;
}
.mbom-issues summary { cursor: pointer; }
.mbom-issues summary em { color: var(--muted); font-style: normal; margin-left: 8px; }

/* 总成标签：和 Excel 底部的工作表标签一个意思，一次只看一个总成 */
.mbom-roots {
  display: flex; gap: 4px; overflow-x: auto;
  padding-bottom: 2px; margin-bottom: 12px;
  border-bottom: 2px solid var(--line);
}
.mbom-roots button {
  display: grid; gap: 1px; flex: 0 0 auto;
  padding: 8px 14px; border: 1px solid var(--line); border-bottom: 0;
  background: var(--surface); color: var(--muted); cursor: pointer; text-align: left;
}
.mbom-roots button b { font: 700 12px var(--body); white-space: nowrap; }
.mbom-roots button em { font-style: normal; font-size: 11px; }
.mbom-roots button.on {
  background: var(--white); color: var(--blue);
  border-color: var(--blue); border-bottom: 2px solid var(--white);
  margin-bottom: -2px;
}

.mbom-toolbar { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 10px; }
.mbom-toolbar input { min-height: 34px; min-width: 240px; padding: 0 10px; border: 1px solid var(--line); border-radius: 4px; font-size: 12px; }
.mbom-current { margin-left: auto; font-size: 12px; }
.mbom-current b { font: 800 13px var(--display); }
.mbom-current em { margin-left: 8px; color: var(--muted); font-style: normal; }

.mbom-hint { margin: 0 0 10px; font-size: 12px; color: var(--muted); }
.mbom-mould-filter { padding: 5px 8px; border: 1px solid var(--line); border-radius: 6px; background: var(--surface); }
.mbom-jump {
  margin-left: 6px; padding: 2px 8px;
  border: 1px solid var(--line); background: var(--white);
  color: var(--blue); font: inherit; font-size: 12px; cursor: pointer;
}

.mbom-section { margin-bottom: 14px; border: 1px solid var(--line); }
.mbom-section > header {
  display: flex; align-items: baseline; gap: 9px; flex-wrap: wrap;
  padding: 8px 12px; border-bottom: 1px solid var(--line); background: var(--surface);
}
.mbom-section > header b { font: 800 13px var(--display); }
.mbom-parent-name { flex: 1 1 auto; min-width: 0; color: var(--ink); font-size: 13px; }
.mbom-section > header em { color: var(--muted); font-style: normal; font-size: 12px; }

.mbom-badge { padding: 2px 8px; font: 800 10px var(--display); color: var(--white); background: var(--blue); }
.mbom-badge.material { background: #6b7f95; }
.mbom-badge.paint { background: #8a5a00; }

.mbom-scroll { overflow-x: auto; }
.mbom-section table { width: 100%; min-width: 900px; border-collapse: collapse; font-size: 12px; }
.mbom-section th, .mbom-section td { padding: 6px 9px; border-bottom: 1px solid var(--line); text-align: left; }
.mbom-section th { color: var(--muted); background: #f3f5f8; white-space: nowrap; }
.mbom-index { color: var(--muted); }
.mbom-qty { white-space: nowrap; font-variant-numeric: tabular-nums; }
/* 只读也要有反馈：不给鼠标样式和悬停提示，点下去就是"卡住了" */
.mbom-qty.is-locked { cursor: help; }
.mbom-qty.is-locked:hover { background: rgba(120, 120, 120, .08); box-shadow: inset 0 0 0 1px rgba(120, 120, 120, .25); }
.mbom-qty small { color: var(--muted); }
/* 不是东禾承接的行标出来——喷漆走东元、电镀走均源，跨法人要单独结算 */
.org-other { color: #8a5a00; font-weight: 800; }
tbody tr.confirm { background: #FFFBEB; }
tbody tr.manual { background: #F4F7FB; }
.mbom-note { color: var(--muted); max-width: 260px; }
.mbom-manual { display: inline-block; margin-right: 5px; padding: 1px 6px; border: 1px solid var(--line); font-size: 10px; font-weight: 800; }

/*
  两个按钮原来都是细线下划线样式、紧挨着，连起来读成了
  「从 EBOM 生成导出 M-BOM」一句话。改成一实一虚、拉开间距。
*/
.mbom-tools { display: flex; align-items: center; gap: 10px; }
.mbom-tools .button { white-space: nowrap; }
.mbom-tools a.button { display: inline-flex; align-items: center; text-decoration: none; }

/* 就地编辑：只开放生成算不准、要人拍板的那几列 */
.cell-edit { cursor: text; }
.cell-edit:hover { background: rgba(0, 117, 174, .07); box-shadow: inset 0 0 0 1px rgba(0, 117, 174, .3); }
/*
  不归自己填的列（0827）：鼠标样式和 hover 都别装成能改。
  点下去仍然有提示说该找谁——静悄悄点不动，人只会以为界面卡了（0821 就吃过这个亏）。
*/
.cell-edit.not-mine { cursor: default; color: var(--muted); }
.cell-edit.not-mine:hover { background: transparent; box-shadow: none; }
/* 表头里那一小行：这一列归谁 */
.mbom-section th em {
  display: block; color: var(--muted); font-style: normal;
  font-size: 10px; font-weight: 400; line-height: 1.4;
}
.cell-edit.is-editing { box-shadow: inset 0 0 0 2px var(--blue); }
.cell-edit input, .cell-edit select {
  width: 100%; min-width: 72px; min-height: 28px; padding: 0 6px;
  border: 1px solid var(--line); border-radius: 3px; font: inherit; font-size: 12px;
}
.cell-edit input:focus, .cell-edit select:focus { border-color: var(--blue); outline: none; }
</style>
